/**
 * Wipes everything in browser storage that belongs to the ACCOUNT rather than
 * to the device, so signing in as somebody else on the same browser starts
 * clean.
 *
 * Why an inverted list: not a single pref key in this app is namespaced by
 * userId or companyId — they are scoped by surface and eventId only. Enumerating
 * the account-bound keys would therefore leave every future key leaking by
 * default. We enumerate the DEVICE-bound ones instead and delete the rest, so a
 * new key is account-bound until somebody deliberately says otherwise.
 *
 * What used to survive a logout, and what it did to the next account:
 *  - `veltric-background-jobs` → another company's event NAMES rendered in the
 *    jobs card, plus polling of foreign job ids that eventually flip to FAILED
 *  - `recentEventIds` → sent as a query param to /events/valid-event-names
 *  - `view-*` / `filter-*` (+ `-adhoc` snapshots) → inherited views and silently
 *    narrowed lists, or a foreign id re-requested on every visit forever
 *  - `workflow-overwrite-warn-hidden:*` → a data-changing confirm suppressed for
 *    a user who never opted out of it
 *  - dismiss-rung keys → the subscription/billing warning hidden for the day
 *  - `surface-default-stamp-*` → the role's default view never applied
 *  - sessionStorage: preview bearer handoff, wizard drafts, velo handoff
 *
 * WHERE THIS MAY RUN — read before adding a call site. Only two doors:
 * the explicit `logout()`, and an account CHANGE detected by the stamp in
 * `hydrateAccountPresentation`. It must NOT hang off `veltric:auth-lost`: that
 * event also fires for a mistyped password on the login screen (the 401
 * interceptor has no auth-route exemption) and for a transient refresh
 * failure — neither means a different human, and the sweep would destroy the
 * current user's unsaved ad-hoc views and every wizard draft in the tab.
 */

import { removePref } from "@/lib/storage/userPrefs";
import { RELOAD_COUNT_KEY, RELOAD_FIRST_KEY } from "@/lib/chunkReload";
import { TABLE_CACHE_RESET_KEY } from "@/lib/storage/resetTableCache";
import { ADHOC_GUESTID_PRUNE_KEY } from "@/lib/storage/pruneAdhocGuestId";
import { resetThemeToDefault } from "@/lib/theme/themeStore";
import { resetLocaleToDefault } from "@/lib/i18n/localeStore";

/** Fired after the sweep for state that lives in memory, not in storage. */
export const ACCOUNT_TEARDOWN_EVENT = "veltric:account-teardown";

/**
 * Identity of the account this browser's stored state belongs to — an
 * ObjectId (userId, or companyId where the lane has no userId), never an
 * email: `e2e_main/specs/leak/localstorage-no-pii.spec.ts` scans every stored
 * value for PII and exempts bare ObjectIds by name.
 */
export const ACCOUNT_STAMP_KEY = "veltric.accountStamp";

/** Device-bound, not account-bound: chrome geometry and one-shot migrations. */
const KEEP_EXACT = new Set<string>([
  "collapsed",
  "viewMode",
  // Rail + panel geometry. Keeping "is the sidebar collapsed" while throwing
  // away "how wide is it" would be an arbitrary line through one feature.
  "crmPaneWidth",
  "appBuilderLeftRail",
  "appBuilderRightRail",
  "app-builder-zoom",
  "slotting-calendar-view",
  "slotting-time-jump",
  "slotting-stats-range",
  // The stamp is how the next login knows whether to sweep at all — sweeping
  // it away would erase the sweep's own memory.
  ACCOUNT_STAMP_KEY,
  // One-shot migration markers — re-running them costs a pointless sweep.
  // Imported, not retyped: the second one is `view-guest-adhoc-guestid-pruned`,
  // and a hand-copied short name silently un-kept it.
  //
  // `TABLE_CACHE_RESET_KEY` succeeded `REQUEST_ADHOC_DROP_KEY` when the
  // one-shot widened from the two request lanes to every surface. Its marker
  // has to be kept for a stronger reason than "a pointless sweep": the reset
  // deletes every `view-*`/`filter-*` ad-hoc entry it finds, so a swept marker
  // makes it re-run on the NEXT account and destroy that account's own fresh
  // snapshots — a migration for state that was never theirs.
  TABLE_CACHE_RESET_KEY,
  ADHOC_GUESTID_PRUNE_KEY,
]);

const KEEP_PREFIXES = ["pageSize-", "seating-zoom-"] as const;

/** sessionStorage keys that describe the TAB, not the account. */
const KEEP_SESSION = new Set<string>([RELOAD_COUNT_KEY, RELOAD_FIRST_KEY]);

function isDeviceScoped(key: string): boolean {
  if (KEEP_EXACT.has(key)) return true;
  return KEEP_PREFIXES.some((p) => key.startsWith(p));
}

function collectDoomed(storage: Storage, keep: (key: string) => boolean): string[] {
  // Collect first: removing while iterating by index re-indexes the store.
  // `length`/`key(i)` (not Object.keys) is what the test stub implements.
  const doomed: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key === null || keep(key)) continue;
    doomed.push(key);
  }
  return doomed;
}

/**
 * Call on an explicit logout, or when the account stamp proves a DIFFERENT
 * account is signing in. Theme and locale are reset through their stores
 * rather than the sweep, so the DOM class and `<html lang>` follow immediately
 * instead of lingering until the next hard reload.
 */
export function clearAccountScopedState(): void {
  if (typeof window === "undefined") return;

  try {
    const ls = window.localStorage;
    // Deleted through `removePref`, not `removeItem`: it also drops the
    // pre-migration cookie twin, without which the next `getPref` promotes
    // the previous account's view/filter straight back in (userPrefs.ts).
    for (const key of collectDoomed(ls, isDeviceScoped)) removePref(key);
  } catch {
    // private mode / quota — nothing we can do
  }

  try {
    const ss = window.sessionStorage;
    for (const key of collectDoomed(ss, (k) => KEEP_SESSION.has(k))) {
      ss.removeItem(key);
    }
  } catch {
    /* noop */
  }

  resetThemeToDefault();
  resetLocaleToDefault();

  try {
    window.dispatchEvent(new Event(ACCOUNT_TEARDOWN_EVENT));
  } catch {
    /* noop */
  }
}
